import { Alert, AlertTitle, Link, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { type PlatformNotification as PlatformNotificationConfig } from '@/helpers/statsig/dynamicConfig/platformNotification';
import { WEB_STORAGE_PREFIX } from '@/utils/constants';
import { safeLocalStorage } from '@/utils/helpers';

export type PlatformNotificationVariant = 'rounded' | 'rectangular';

/** Presentational banner for a single platform-wide notification. */
export const PlatformNotification = memo(function PlatformNotification({
  notification,
  variant = 'rounded',
}: {
  notification: PlatformNotificationConfig;
  variant?: PlatformNotificationVariant;
}) {
  const dismissalKey = `${WEB_STORAGE_PREFIX}notification::${notification.id}`;
  // The dismissal stores the message it was made against, so new copy shows up again.
  const [dismissedMessage, setDismissedMessage] = useState(() => safeLocalStorage.get(dismissalKey));

  if (notification.dismissible && dismissedMessage === notification.message) {
    return null;
  }

  const handleClose = () => {
    safeLocalStorage.set(dismissalKey, notification.message);
    setDismissedMessage(notification.message);
  };

  return (
    <Alert
      variant="standard"
      severity={notification.severity}
      onClose={notification.dismissible ? handleClose : undefined}
      sx={variant === 'rectangular' ? { borderRadius: 0 } : undefined}
    >
      {notification.title && <AlertTitle>{notification.title}</AlertTitle>}
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
        }}
      >
        {notification.message}
        {notification.linkHref && notification.linkLabel && (
          <>
            {' '}
            <Link href={notification.linkHref} target="_blank" rel="noreferrer" variant="body2" underline="always">
              {notification.linkLabel}
            </Link>
          </>
        )}
      </Typography>
    </Alert>
  );
});
